import { Component } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

/**
 * ErrorBoundary - Catches JavaScript errors in child components
 * and displays a fallback UI instead of crashing the entire page.
 * 
 * @example
 * <ErrorBoundary>
 *   <MyComponent />
 * </ErrorBoundary>
 * 
 * @example
 * // With custom fallback
 * <ErrorBoundary fallback={<CustomErrorUI />}>
 *   <MyComponent />
 * </ErrorBoundary>
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render shows the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to console for debugging
    console.error('ErrorBoundary caught an error:', error);
    console.error('Error info:', errorInfo);
    
    this.setState({ errorInfo });

    // Call optional onError callback
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });

    // Call optional onReset callback
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  render() {
    const { hasError, error } = this.state;
    const { children, fallback } = this.props;

    if (hasError) {
      // If a custom fallback is provided, use it
      if (fallback) {
        return fallback;
      }

      // Default fallback UI
      return (
        <div className="
          flex flex-col items-center justify-center
          min-h-[200px] p-8
          bg-background
          text-center
        ">
          <div className="
            w-16 h-16 mb-4
            flex items-center justify-center
            rounded-full
            bg-danger/10
          ">
            <AlertTriangle className="w-8 h-8 text-danger" />
          </div>
          
          <h2 className="text-xl font-semibold text-foreground mb-2">
            Something went wrong
          </h2>
          
          <p className="text-foreground-secondary mb-6 max-w-md">
            An unexpected error occurred. Please try again or contact support if the problem persists.
          </p>

          {/* Show error message in development */}
          {import.meta.env.DEV && error && (
            <div className="
              mb-6 p-4 max-w-lg w-full
              bg-card border border-border rounded-md
              text-left overflow-auto
            ">
              <p className="text-sm font-mono text-danger">
                {error.toString()}
              </p>
            </div>
          )}

          <button
            onClick={this.handleReset}
            className="
              inline-flex items-center gap-2
              px-4 py-2
              bg-primary hover:bg-primary-hover
              text-background font-medium
              rounded-md
              transition-colors duration-200
              focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background
            "
          >
            <RefreshCw className="w-4 h-4" />
            Try Again
          </button>
        </div>
      );
    }

    return children;
  }
}

/**
 * withErrorBoundary - HOC to wrap a component with ErrorBoundary
 * 
 * @param {React.ComponentType} WrappedComponent - Component to wrap
 * @param {Object} [errorBoundaryProps] - Props to pass to ErrorBoundary
 * @returns {React.ComponentType} - Wrapped component
 * 
 * @example
 * const SafeComponent = withErrorBoundary(MyComponent);
 */
export function withErrorBoundary(WrappedComponent, errorBoundaryProps = {}) {
  const displayName = WrappedComponent.displayName || WrappedComponent.name || 'Component';
  
  function WithErrorBoundary(props) {
    return (
      <ErrorBoundary {...errorBoundaryProps}>
        <WrappedComponent {...props} />
      </ErrorBoundary>
    );
  }
  
  WithErrorBoundary.displayName = `withErrorBoundary(${displayName})`;
  
  return WithErrorBoundary;
}

export { ErrorBoundary };
export default ErrorBoundary;
